// ─── Authra Design System v2 ───────────────────────────
// Primary:   #0A2342  deep navy (the Authra brand colour)
// Accent:    #0F9B8E  teal
// Gold:      #D69E2E  amber/gold (secondary brand)
// Font:      Inter (UI + headings)
// Radius:    8px standard, 12px cards
// Grid:      8px base unit — all spacing multiples of 8
// ──────────────────────────────────────────────────────

import { createTheme, alpha } from '@mui/material/styles';

const NAVY        = '#0A2342';
const NAVY_DEEP   = '#080F1E';
const TEAL        = '#0A7D72';   // was #0F9B8E; darkened for readable text (5.0:1 on white)
const TEAL_LIGHT  = '#E0F5F3';
const TEAL_DARK   = '#0A6E63';
const GOLD        = '#D69E2E';
const GOLD_LIGHT  = '#FEF9EC';
const BG_PAGE     = '#F5F7FA';
const BG_CARD     = '#FFFFFF';
const BORDER      = '#E4E9F0';
const BORDER_DARK = '#C8D0DC';
const TEXT_PRIMARY   = '#0D1B2A';
const TEXT_SECONDARY = '#5A6A7E';
const TEXT_MUTED     = '#5B6B7F';   // was #8896A7 (3.0:1); now 5.4:1 on white

const authrTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main:         NAVY,
      light:        '#1A3A5C',
      dark:         '#060D18',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main:         TEAL,
      light:        TEAL_LIGHT,
      dark:         TEAL_DARK,
      contrastText: '#FFFFFF',
    },
    warning: {
      main:         '#92580A',   // was GOLD (#D69E2E): 2.4:1 as text; now 5.9:1 on white, 5.1:1 on tinted rows
      light:        GOLD_LIGHT,
      dark:         '#8A6514',
      contrastText: '#FFFFFF',
    },
    success: {
      main:         '#157347',
      light:        '#E6F7F1',
      dark:         '#0F6040',
      contrastText: '#FFFFFF',
    },
    error: {
      main:         '#C02B2B',
      light:        '#FDEAEA',
      dark:         '#A02020',
      contrastText: '#FFFFFF',
    },
    info: {
      main:         TEAL,
      light:        TEAL_LIGHT,
      dark:         TEAL_DARK,
      contrastText: '#FFFFFF',
    },
    background: {
      default: BG_PAGE,
      paper:   BG_CARD,
    },
    text: {
      primary:   TEXT_PRIMARY,
      secondary: TEXT_SECONDARY,
      disabled:  TEXT_MUTED,
    },
    divider: BORDER,
  },

  typography: {
    fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
    fontWeightLight:   300,
    fontWeightRegular: 400,
    fontWeightMedium:  500,
    fontWeightBold:    600,

    h1: {
      fontFamily:    "'Inter', sans-serif",
      fontSize:      '1.75rem',
      fontWeight:    700,
      // colour inherits (readable on white and on navy cards); was color:         NAVY,
      lineHeight:    1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily:    "'Inter', sans-serif",
      fontSize:      '1.375rem',
      fontWeight:    700,
      // colour inherits (readable on white and on navy cards); was color:         NAVY,
      lineHeight:    1.25,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily:    "'Inter', sans-serif",
      fontSize:      '1.125rem',
      fontWeight:    600,
      // colour inherits (readable on white and on navy cards); was color:         TEXT_PRIMARY,
      lineHeight:    1.3,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontFamily:    "'Inter', sans-serif",
      fontSize:      '1rem',
      fontWeight:    600,
      // colour inherits (readable on white and on navy cards); was color:         TEXT_PRIMARY,
      lineHeight:    1.4,
    },
    h5: {
      fontFamily:    "'Inter', sans-serif",
      fontSize:      '0.875rem',
      fontWeight:    600,
      // colour inherits (readable on white and on navy cards); was color:         TEXT_PRIMARY,
      lineHeight:    1.4,
    },
    h6: {
      fontFamily:    "'Inter', sans-serif",
      fontSize:      '0.6875rem',
      fontWeight:    600,
      // colour inherits (readable on white and on navy cards); was color:         TEXT_SECONDARY,
      lineHeight:    1.4,
      letterSpacing: '0.07em',
      textTransform: 'uppercase',
    },
    body1: {
      fontSize:   '0.875rem',
      fontWeight: 400,
      lineHeight: 1.6,
      // colour inherits (readable on white and on navy cards); was color:      TEXT_PRIMARY,
    },
    body2: {
      fontSize:   '0.8125rem',
      fontWeight: 400,
      lineHeight: 1.5,
      // colour inherits (readable on white and on navy cards); was color:      TEXT_SECONDARY,
    },
    subtitle1: {
      fontSize:   '0.875rem',
      fontWeight: 500,
      // colour inherits (readable on white and on navy cards); was color:      TEXT_PRIMARY,
    },
    subtitle2: {
      fontSize:   '0.8125rem',
      fontWeight: 500,
      // colour inherits (readable on white and on navy cards); was color:      TEXT_SECONDARY,
    },
    caption: {
      fontSize:      '0.6875rem',
      fontWeight:    400,
      // colour inherits (readable on white and on navy cards); was color:         TEXT_MUTED,
      letterSpacing: '0.01em',
    },
    overline: {
      fontSize:      '0.6875rem',
      fontWeight:    600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      // colour inherits (readable on white and on navy cards); was color:         TEXT_SECONDARY,
    },
    button: {
      fontFamily:    "'Inter', sans-serif",
      fontWeight:    500,
      fontSize:      '0.8125rem',
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
  },

  shape: { borderRadius: 8 },

  shadows: [
    'none',
    '0 1px 2px rgba(10,35,66,0.06)',
    '0 2px 4px rgba(10,35,66,0.07)',
    '0 4px 8px rgba(10,35,66,0.08)',
    '0 6px 16px rgba(10,35,66,0.09)',
    '0 8px 24px rgba(10,35,66,0.10)',
    ...Array(19).fill('none'),
  ],

  components: {

    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily:      "'Inter', 'Helvetica Neue', Arial, sans-serif",
          backgroundColor: BG_PAGE,
          color:           TEXT_PRIMARY,
          WebkitFontSmoothing:  'antialiased',
          MozOsxFontSmoothing:  'grayscale',
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius:  8,
          textTransform: 'none',
          fontWeight:    500,
          fontSize:      '0.8125rem',
          letterSpacing: '0.01em',
          transition:    'all 0.15s ease',
        },
        contained: {
          boxShadow: '0 1px 2px rgba(10,35,66,0.12)',
          '&:hover': {
            boxShadow:  '0 4px 12px rgba(10,35,66,0.18)',
            transform:  'translateY(-1px)',
          },
          '&:active': {
            transform:  'translateY(0)',
            boxShadow:  '0 1px 2px rgba(10,35,66,0.12)',
          },
        },
        outlined: {
          borderColor: BORDER_DARK,
          '&:hover': {
            borderColor:     NAVY,
            backgroundColor: alpha(NAVY, 0.04),
          },
        },
        text: {
          '&:hover': { backgroundColor: alpha(NAVY, 0.04) },
        },
        sizeSmall: {
          fontSize:  '0.75rem',
          padding:   '4px 10px',
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius:  12,
          border:        `0.5px solid ${BORDER}`,
          boxShadow:     'none',
          transition:    'box-shadow 0.15s ease, border-color 0.15s ease',
          '&:hover': { borderColor: BORDER_DARK },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow:       'none',
          border:          `0.5px solid ${BORDER}`,
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(10,35,66,0.07)',
          border:    'none',
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily:    "'Inter', sans-serif",
          fontWeight:    500,
          fontSize:      '0.6875rem',
          borderRadius:  20,
          height:        22,
          letterSpacing: '0.01em',
        },
        label: { padding: '0 8px' },
        sizeSmall: {
          height:   20,
          fontSize: '0.6875rem',
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontFamily:      "'Inter', sans-serif",
            fontWeight:      600,
            fontSize:        '0.6875rem',
            letterSpacing:   '0.05em',
            textTransform:   'uppercase',
            color:           TEXT_SECONDARY,
            backgroundColor: '#F8FAFC',
            borderBottom:    `1px solid ${BORDER}`,
            padding:         '10px 16px',
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily:  "'Inter', sans-serif",
          fontSize:    '0.8125rem',
          color:       TEXT_PRIMARY,
          borderColor: BORDER,
          padding:     '12px 16px',
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.1s ease',
          '&:hover': { backgroundColor: '#F8FAFC' },
          '&:last-child td': { border: 0 },
        },
      },
    },

    MuiTextField: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            fontSize:     '0.875rem',
            '& fieldset':             { borderColor: BORDER },
            '&:hover fieldset':       { borderColor: BORDER_DARK },
            '&.Mui-focused fieldset': { borderColor: TEAL, borderWidth: '1.5px' },
          },
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        // Slide-out panels (firm details, filings, audit, etc.) are white so
        // their text is readable. Only the left-hand sidebar drawer is navy.
        paper: {
          backgroundColor: BG_CARD,
          color:           TEXT_PRIMARY,
          borderRight:     'none',
          boxShadow:       '-2px 0 20px rgba(0,0,0,0.15)',
        },
        paperAnchorLeft: {
          backgroundColor: NAVY_DEEP,
          boxShadow:       '2px 0 20px rgba(0,0,0,0.15)',
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border:       '0.5px solid',
          fontSize:     '0.8125rem',
        },
        standardSuccess: {
          backgroundColor: '#E6F7F1',
          borderColor:     '#A7DFC8',
          color:           '#0F6040',
        },
        standardWarning: {
          backgroundColor: GOLD_LIGHT,
          borderColor:     '#F0CC7A',
          color:           '#7A5000',
        },
        standardError: {
          backgroundColor: '#FDEAEA',
          borderColor:     '#F5AAAA',
          color:           '#8B1A1A',
        },
        standardInfo: {
          backgroundColor: TEAL_LIGHT,
          borderColor:     '#7FD4CE',
          color:           TEAL_DARK,
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily:    "'Inter', sans-serif",
          fontWeight:    500,
          fontSize:      '0.8125rem',
          textTransform: 'none',
          letterSpacing: '0.01em',
          minHeight:     44,
          color:         TEXT_SECONDARY,
          '&.Mui-selected': {
            color:      NAVY,
            fontWeight: 600,
          },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: TEAL,
          height:          2.5,
          borderRadius:    '2px 2px 0 0',
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          border:       `0.5px solid ${BORDER}`,
          boxShadow:    '0 20px 60px rgba(10,35,66,0.15)',
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height:       6,
          backgroundColor: alpha(TEAL, 0.12),
        },
        bar: { borderRadius: 4 },
      },
    },

    MuiSkeleton: {
      styleOverrides: {
        root: { borderRadius: 6 },
        wave: {
          '&::after': {
            background: `linear-gradient(90deg, transparent, ${alpha(NAVY, 0.04)}, transparent)`,
          },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: NAVY,
          fontSize:        '0.6875rem',
          fontFamily:      "'Inter', sans-serif",
          borderRadius:    6,
          padding:         '5px 10px',
        },
        arrow: { color: NAVY },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: { borderColor: BORDER },
      },
    },

    MuiBadge: {
      styleOverrides: {
        badge: {
          fontSize:   '0.625rem',
          fontWeight: 600,
          minWidth:   16,
          height:     16,
          padding:    '0 4px',
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          transition:   'all 0.15s ease',
          '&:hover': { backgroundColor: alpha(NAVY, 0.06) },
        },
        sizeSmall: { padding: 4 },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin:       '1px 8px',
          '&.Mui-selected': {
            backgroundColor: alpha(TEAL, 0.1),
            '&:hover': { backgroundColor: alpha(TEAL, 0.15) },
          },
        },
      },
    },

  },
});

export default authrTheme;

// ── Compatibility shim — legacy named export used by Layout and other pages ──
export const tokens = {
  // Brand (updated to new design system)
  brandBlue:      NAVY,
  brandBlueDark:  '#060D18',
  brandBlueLight: '#1A3A5C',

  // Sidebar (updated)
  sidebarBg:         NAVY_DEEP,
  sidebarActive:     'rgba(15,155,142,0.08)',
  sidebarHover:      'rgba(255,255,255,0.06)',
  sidebarText:       'rgba(255,255,255,0.55)',
  sidebarActiveText: '#FFFFFF',
  sidebarIcon:       'rgba(255,255,255,0.55)',
  sidebarActiveIcon: '#FFFFFF',

  // Surfaces
  bgMain:  BG_PAGE,
  bgCard:  BG_CARD,
  border:  BORDER,
  divider: BORDER,

  // Text
  textPrimary:   TEXT_PRIMARY,
  textSecondary: TEXT_SECONDARY,
  textMuted:     TEXT_MUTED,

  // Semantic
  successGreen:  '#1B8A5A',
  successLight:  '#E6F7F1',
  warningAmber:  GOLD,
  warningLight:  GOLD_LIGHT,
  errorRed:      '#C02B2B',
  errorLight:    '#FDEAEA',
  infoBlue:      TEAL,
  infoLight:     TEAL_LIGHT,

  // RAG
  ragGreen: '#1B8A5A',
  ragAmber: GOLD,
  ragRed:   '#C02B2B',
  ragGrey:  TEXT_MUTED,
};
